import { luminosity } from '@mentimeter/ragnar-colors';
import type { QuestionGrid } from '@mentimeter/http-clients';
import type { BaseType } from 'd3-selection';
import { scaleLinear, type ScaleLinear } from 'd3-scale';
import { line as d3Line } from 'd3-shape';
import { d3, shouldUpdateText, wordWrap } from './d3';
import { SMALL_FONT_SIZE, NORMAL_FONT_SIZE, MEDIUM_FONT_SIZE } from './utils';

const LAYOUT = {
  width: 960,
  height: 540,
};
const MARGIN = {
  top: LAYOUT.height * 0.12,
  right: LAYOUT.width * 0.05,
  bottom: LAYOUT.height * 0.07,
  left: LAYOUT.width * 0.05,
};
const MAX_VOTES_THRESHOLD = 200;
const VIRTUAL_TEXT_BOX_HEIGHT = 150;
const AXIS_PADDING = 35;
const SMALL_LEGEND_PADDING = 75;
const LEGEND_PADDING = 100;
const VIRTUAL_TEXT_BOX_WIDTH = LAYOUT.width - LAYOUT.height - LEGEND_PADDING;
const LEGEND_TEXT_PADDING = 30;
const BALL_RADIUS = 20;
const BALL_HOVER_SCALE = 1.1;
const SCATTER_BALL_RADIUS = 7;
const SCATTER_LINE_STROKE_WIDTH = 2;
const UNFOCUSED_BALL_OPACITY = 0.3;
const ALPHA_MIN = 0.04;
const VELOCITY_DECAY = 0.25;
const VELOCITY_DECAY_SCATTER = 0.4;
const SCREENSHOT_NUM_FORCE_TICKS = 100;

export type Grid = QuestionGrid;

interface RatingGridPositionAndSize {
  top: number;
  right: number;
  bottom: number;
  left: number;
  width: number;
  height: number;
}

export interface RatingLabel {
  label: string;
  xPos: number;
  yPos: number;
  xOffset: number;
  yOffset: number;
  rotation: number;
  min: string;
  max: string;
}

type Score = [number, number] | [null, null];

export interface RatingResultT {
  id: number;
  label: string;
  score: Score;
}

interface Range {
  min: number;
  max: number;
}

interface Node extends d3.SimulationNodeDatum {
  id: number;
  /**
   * It's possible for the node's `index` to not be initially
   * provided. Instead, it's set by the `update` function.
   * @see {Rating.updateBalls}
   */
  index?: number | undefined;
  label: string;
  score: Score;
}

export interface ScatterNode extends Node {
  color: string;
  vote: [number, number] | [] | null;
}

export interface RatingProps {
  selector: string;
  gridPositionAndSize: RatingGridPositionAndSize;
  labels: RatingLabel[];
  textColor: string;
  lineColor: string;
  is4Values: boolean;
  backgroundColor: string;
  grid: Grid;
  results: RatingResultT[];
  respondents: number;
  enableAnimations: boolean;
  range: Range;
  nodes: Node[];
  scatterNodes: ScatterNode[];
  colors: string[];
  textColors: string[];
  hoverId: number | null;
  setHoverId: (id: number | null) => void;
}

/**
 * 2x2 Grid: matrix visualization to evaluate items based on two axes (e.g., Effort vs. Value)
 */
export class Rating implements RatingProps {
  readonly selector: string;
  readonly gridPositionAndSize: RatingGridPositionAndSize;
  readonly labels: RatingLabel[];
  readonly textColor: string;
  readonly lineColor: string;
  readonly is4Values: boolean;
  readonly backgroundColor: string;
  readonly grid: Grid;
  readonly results: RatingResultT[];
  readonly respondents: number;
  readonly enableAnimations: boolean;
  readonly range: Range;
  readonly nodes: Node[];
  readonly scatterNodes: ScatterNode[];
  readonly colors: string[];
  readonly textColors: string[];
  readonly hoverId: number | null;
  readonly setHoverId: (id: number | null) => void;
  readonly force: d3.Simulation<Node, undefined> = d3
    .forceSimulation<Node>()
    .alphaMin(ALPHA_MIN) // Prevent excessive ticks, which could impact performance
    .velocityDecay(VELOCITY_DECAY); // Allow slight passing through target destination, adds "bounce" effect
  readonly scatterForce: d3.Simulation<ScatterNode, undefined> = d3
    .forceSimulation<ScatterNode>()
    .alphaMin(ALPHA_MIN)
    .velocityDecay(VELOCITY_DECAY_SCATTER);
  readonly x: ScaleLinear<number, number> = scaleLinear();
  readonly y: ScaleLinear<number, number> = scaleLinear();

  /**
   * @param selector: string selection to a dom node, e.g., #rating-${questionId}
   * @param gridPositionAndSize: object describing the dimensions and position of the background grid
   * @param labels: array of label objects for the axes
   * @param is4Values: boolean sub type that decides some aspects of the layout
   * @param textColor: color string, e.g., #e8e8e8
   * @param lineColor: color string, e.g., #e8e8e8
   * @param backgroundColor: color string, e.g., #e8e8e8
   * @param grid: active grid  with background assets
   * @param range: range of minimum and maximum values for the x and y axis
   * @param results: result data that can be visualized in the matrix
   * @param respondents: number of participants that have voted on the slide
   * @param enableAnimations: boolean to decide whether animations should be rushed
   * @param nodes: list of descriptive data for nodes in the visualization
   * @param scatterNodes: list of descriptive data for distribution in the visualization
   * @param colors: list of colors for nodes, e.g., ['green', 'yellow', 'blue']
   * @param textColors: list of colors for readable text, e.g., ['white', 'black', 'white']
   * @param hoverId: corresponds to an option id that is set when the mouse is hovering
   * @param setHoverId: setter to decide which option should be highlighted
   */
  constructor(props: RatingProps) {
    this.selector = props.selector;
    this.gridPositionAndSize = props.gridPositionAndSize;
    this.labels = props.labels;
    this.is4Values = props.is4Values;
    this.textColor = props.textColor;
    this.lineColor = props.lineColor;
    this.backgroundColor = props.backgroundColor;
    this.grid = props.grid;
    this.range = props.range;
    this.results = props.results;
    this.respondents = props.respondents;
    this.enableAnimations = props.enableAnimations;
    this.nodes = props.nodes;
    this.scatterNodes = props.scatterNodes;
    this.colors = props.colors;
    this.textColors = props.textColors;
    this.hoverId = props.hoverId;
    this.setHoverId = props.setHoverId;
  }
  /**
   * Sets the lines and arrow for the x and y- axis
   */
  private updateLines = (): void => {
    const { is4Values, lineColor } = this;
    const { width: gridSize } = this.gridPositionAndSize;
    const halfGridSize = gridSize / 2;
    // First value corresponds x-axis
    // Second value corresponds y-axis
    const axisData = [
      {
        x1: 0,
        y1: is4Values ? halfGridSize : gridSize,
        x2: gridSize,
        y2: is4Values ? halfGridSize : gridSize,
        'marker-start': is4Values ? 'url(#arrowLeftAndUp)' : 'none',
        'marker-end': 'url(#arrowRightAndDown)',
      },
      {
        x1: is4Values ? halfGridSize : 0,
        y1: 0,
        x2: is4Values ? halfGridSize : 0,
        y2: gridSize,
        'marker-start': 'url(#arrowLeftAndUp)',
        'marker-end': is4Values ? 'url(#arrowRightAndDown)' : 'none',
      },
    ] as const;

    // The x- and y-axis lines
    d3.select(this.selector)
      .selectAll('line')
      .data(axisData)
      .join('line')
      .attr('class', 'axis-lines')
      .style('stroke', lineColor)
      .attr('x1', (d) => d.x1) // x position of the first end of the line
      .attr('y1', (d) => d.y1) // y position of the first end of the line
      .attr('x2', (d) => d.x2) // x position of the second end of the line
      .attr('y2', (d) => d.y2) // y position of the second end of the line
      .attr('stroke-width', 3)
      .attr('marker-start', (d) => d['marker-start'])
      .attr('marker-end', (d) => d['marker-end']);

    const markerData = [
      { id: 'arrowRightAndDown', orient: 'auto' },
      { id: 'arrowLeftAndUp', orient: 'auto-start-reverse' },
    ] as const satisfies readonly {
      readonly id: string;
      readonly orient: string;
    }[];

    // Arrows
    d3.select(this.selector)
      .selectAll('marker')
      .data(markerData)
      .join('svg:marker')
      .attr('id', (d) => d.id)
      .attr('viewBox', '0 0 10 10')
      .attr('refX', 0)
      .attr('refY', 5)
      .attr('orient', (d) => d.orient)
      .attr('markerUnits', 'strokeWidth')
      .attr('markerWidth', 8)
      .attr('markerHeight', 6)
      .append('svg:path')
      .attr('d', 'M 0 1 L 11 5 L 0 9 z')
      .style('fill', lineColor);
  };
  /**
   * Controls the labels that surround the axis
   */
  private updateAxis = (): void => {
    const { is4Values } = this;
    const { width: gridSize } = this.gridPositionAndSize;

    // Select
    const axisLabels = d3
      .select(this.selector)
      .selectAll<SVGTextElement, BaseType>('.axis-label')
      .data(this.labels);

    // Enter
    const labelEnter = axisLabels
      .enter()
      .append<SVGTextElement>('g')
      .attr('class', 'axis-label');
    const labelText = labelEnter
      .append('text')
      .attr('class', 'main-label')
      .attr('text-anchor', 'middle')
      .attr('font-size', MEDIUM_FONT_SIZE);
    const labelHigh = labelEnter
      .append('text')
      .attr('class', 'high-label')
      .attr('text-anchor', 'end')
      .attr('dy', (_, i) => (i === 0 ? '0em' : '0.65em'))
      .attr('transform', `translate(${gridSize / 2}, 0)`)
      .attr('opacity', 0);
    const labelLow = labelEnter
      .append('text')
      .attr('class', 'low-label')
      .attr('dy', (_, i) => (i === 0 ? '0em' : '0.65em'))
      .attr('transform', `translate(${-gridSize / 2}, 0)`)
      .attr('opacity', 0);

    // Update
    axisLabels
      .merge(labelEnter)
      .attr(
        'transform',
        (d) => `translate(${d.xPos + d.xOffset}, ${d.yPos + d.yOffset})
      rotate(${d.rotation})`,
      )
      .on('mouseover', mouseOver)
      .on('mouseout', mouseOut);
    const mainLabelSelection = axisLabels
      .select<SVGTextElement>('.main-label')
      .merge(labelText);
    mainLabelSelection.attr('fill', this.textColor);
    if (
      shouldUpdateText<SVGTextElement, RatingLabel>({
        selection: mainLabelSelection,
        texts: this.labels.map((d) => d.label),
        maxWidth: gridSize / 2,
        maxHeight: VIRTUAL_TEXT_BOX_HEIGHT,
      })
    ) {
      mainLabelSelection
        .text((d) => d.label)
        .call(wordWrap, {
          maxWidth: gridSize / 2,
          maxHeight: VIRTUAL_TEXT_BOX_HEIGHT,
          fontSize: MEDIUM_FONT_SIZE,
        })
        .attr('y', majorLabelYPos);
    }
    const highLabelSelection = axisLabels
      .select<SVGTextElement>('.high-label')
      .merge(labelHigh);
    highLabelSelection.attr('fill', this.textColor);
    if (
      shouldUpdateText({
        selection: highLabelSelection,
        texts: highLabelSelection.data().map((d) => d.max),
        maxWidth: gridSize / 4,
        maxHeight: VIRTUAL_TEXT_BOX_HEIGHT,
      })
    ) {
      highLabelSelection
        .text((d) => d.max)
        .call(wordWrap, {
          maxWidth: gridSize / 4,
          maxHeight: VIRTUAL_TEXT_BOX_HEIGHT,
          fontSize: SMALL_FONT_SIZE,
        })
        .attr('dy', minorLabelDy);
    }
    const lowLabelSelection = axisLabels
      .select<SVGTextElement>('.low-label')
      .merge(labelLow);
    lowLabelSelection.attr('fill', this.textColor);
    if (
      shouldUpdateText({
        selection: lowLabelSelection,
        texts: lowLabelSelection.data().map((d) => d.min),
        maxWidth: gridSize / 4,
        maxHeight: VIRTUAL_TEXT_BOX_HEIGHT,
      })
    ) {
      lowLabelSelection
        .text((d) => d.min)
        .call(wordWrap, {
          maxWidth: gridSize / 4,
          maxHeight: VIRTUAL_TEXT_BOX_HEIGHT,
          fontSize: SMALL_FONT_SIZE,
        })
        .attr('dy', minorLabelDy);
    }
    // Exit
    axisLabels.exit().remove();

    const highAndLowLabels = d3
      .select(this.selector)
      .selectAll('.high-label, .low-label');
    function mouseOver() {
      highAndLowLabels.transition().attr('opacity', is4Values ? 0 : 1);
    }
    function mouseOut() {
      highAndLowLabels.transition().attr('opacity', 0);
    }
    // Re: majorLabelDy and minorLabelDy:
    // Displace text if wrapping onto next line and in position to interfere with chart
    function majorLabelYPos(
      this: SVGGraphicsElement,
      _: unknown,
      i: keyof any,
    ) {
      const isTopOrLeftLabel = is4Values ? i === 0 || i === 3 : i === 1;
      if (isTopOrLeftLabel) {
        // Adjusts the text's positioning based on its computed height when rendered
        // Allow `this` as it does not refer to component instance, it refers to the d3 node selection
        return -this.getBBox().height + AXIS_PADDING;
      }
      return '0.35em';
    }
    function minorLabelDy(this: SVGGraphicsElement, _: unknown, i: keyof any) {
      if (this.childNodes.length > 1 && i === 1) {
        return '-0.35em';
      }
      return i === 0 ? '0em' : '0.65em';
    }
  };

  /**
   * Appends the background to the grid
   */
  private updateGrid = (): void => {
    const { height, width, top, left } = this.gridPositionAndSize;
    const isDarkTheme = luminosity(this.backgroundColor) < 0.5;
    let gridUrl = this.grid
      ? isDarkTheme
        ? this.grid.bright
        : this.grid.dark
      : '';

    if (
      this.grid &&
      this.grid.type === 'custom' &&
      this.grid.cropped_image_url
    ) {
      gridUrl = this.grid.cropped_image_url;
    }

    d3.select(this.selector)
      .selectAll('.background')
      .data(['']) // Always append only one element
      .enter()
      .insert('g', ':first-child')
      .attr('class', 'background');

    // Select
    const image = d3
      .select(this.selector)
      .select('.background')
      .selectAll<SVGImageElement, [typeof gridUrl]>('image')
      .data([gridUrl]);
    // Enter
    const imageEnter = image
      .enter()
      .append('image')
      .attr('x', left)
      .attr('y', top)
      .attr('height', height)
      .attr('width', width);
    // Update
    image
      .merge(imageEnter)
      .attr('xlink:href', (d) => d)
      .attr('transform', () => {
        let xPos = 0;
        let yPos = 0;
        const rotation =
          this.grid && Boolean(this.grid.rotation)
            ? parseInt(String(this.grid.rotation), 10)
            : 0;
        if (rotation === 90) {
          yPos = -height;
        } else if (rotation === 180) {
          xPos = -width;
          yPos = -height;
        } else if (rotation === 270) {
          xPos = -width;
        }
        return `rotate(${rotation}) translate(${xPos}, ${yPos})`;
      });
    // Exit
    image.exit().remove();
  };

  /**
   * Builds the legend and its interactions
   */
  private updateLegend = (): void => {
    let legendBallRadius = 8;
    let legendLabelPadding = 7;
    let ballLabelFontSize = 14;
    const numResults = this.results.length;
    if (numResults < 7) {
      legendBallRadius = 18;
      legendLabelPadding = 23;
      ballLabelFontSize = 20;
    } else if (numResults < 12) {
      legendBallRadius = 13;
      legendLabelPadding = 17;
      ballLabelFontSize = 17;
    } else if (numResults < 15) {
      legendBallRadius = 10;
      legendLabelPadding = 14;
      ballLabelFontSize = 14;
    }
    const virtualTextBoxHeight = legendBallRadius * 2 * 1.5;
    const legendPadding = this.is4Values
      ? LEGEND_PADDING
      : SMALL_LEGEND_PADDING;

    d3.select(this.selector)
      .selectAll('.legend')
      .data([''])
      .join('g')
      .attr('class', 'legend');

    // Select
    const legendItem = d3
      .select('.legend')
      .attr(
        'transform',
        `translate(
        ${this.gridPositionAndSize.width + legendPadding},
        ${legendBallRadius}
      )`,
      )
      .selectAll<SVGGElement, typeof this.results>('.legend-item')
      .data(this.results);
    // Enter
    const legendItemEnter = legendItem
      .enter()
      .append<SVGGElement>('g')
      .attr('class', 'legend-item')
      .attr(
        'transform',
        (_, i) =>
          `translate(0, ${i * (legendBallRadius * 2 + legendLabelPadding)})`,
      )
      .attr('opacity', 1);
    const legendCircle = legendItemEnter
      .append('circle')
      .attr('class', 'legend-circle');
    const legendNumber = legendItemEnter
      .append('text')
      .text((_, i) => i + 1)
      .attr('class', 'number-label')
      .attr('dy', '0.35em')
      .attr('text-anchor', 'middle');
    const legendLabel = legendItemEnter
      .append('text')
      .attr('class', 'legend-label')
      .attr('x', LEGEND_TEXT_PADDING)
      .attr('dy', '0.35em');
    const legendRect = legendItemEnter
      .append('rect') // Transparent rectangle for mouse events
      .attr('width', VIRTUAL_TEXT_BOX_WIDTH)
      .attr('fill', 'transparent');
    // Update
    legendItem
      .merge(legendItemEnter)
      .attr(
        'transform',
        (_, i) =>
          `translate(0, ${i * (legendBallRadius * 2 + legendLabelPadding)})`,
      )
      .transition()
      .attr('opacity', (d) =>
        this.hoverId && this.hoverId !== d.id ? UNFOCUSED_BALL_OPACITY : 1,
      );
    legendItem
      .select<SVGCircleElement>('.legend-circle')
      .merge(legendCircle)
      .attr('r', legendBallRadius)
      .attr('fill', (_, i) => this.colors[i] ?? null);
    legendItem
      .select<SVGTextElement>('.number-label')
      .merge(legendNumber)
      .attr('font-size', ballLabelFontSize)
      .attr('fill', (_, i) => this.textColors[i] ?? null);
    legendItem
      .select<SVGTextElement>('.number-label')
      .merge(legendNumber)
      .text((_, i) => i + 1)
      .attr('font-size', ballLabelFontSize);
    const selection = legendItem
      .select<SVGTextElement>('.legend-label')
      .merge(legendLabel);
    if (
      shouldUpdateText({
        selection,
        texts: selection.data().map((d) => d.label),
        maxWidth: VIRTUAL_TEXT_BOX_WIDTH,
        maxHeight: virtualTextBoxHeight,
      })
    ) {
      selection
        .text((d) => d.label)
        .call(wordWrap, {
          maxWidth: VIRTUAL_TEXT_BOX_WIDTH,
          maxHeight: virtualTextBoxHeight,
          fontSize: NORMAL_FONT_SIZE,
        });
    }
    selection.attr('fill', this.textColor);
    legendItem
      .select<SVGRectElement>('rect')
      .merge(legendRect)
      .attr('height', virtualTextBoxHeight)
      .attr('x', -legendBallRadius)
      .attr('y', -legendBallRadius)
      .on('mouseover', (_event, d) => {
        const node = this.nodes.find((n) => n.id === d.id);
        if (node) {
          this.mouseOver(node);
        }
      })
      .on('mouseout', () => this.setHoverId(null));
    // Exit
    legendItem.exit().remove();
  };

  /**
   * Creates the distribution of votes, visible on node mouseover
   */
  private updateScatter = (): void => {
    const showScatter = this.hoverId !== null || this.nodes.length === 1;
    const line = d3Line()
      .x((d) => d[0])
      .y((d) => d[1]);

    d3.select(this.selector)
      .selectAll('.scatter-container')
      .data([''])
      .join('g')
      .attr('class', 'scatter-container');

    // Select
    const scatter = d3
      .select('.scatter-container')
      .selectAll<SVGGElement, typeof this.scatterNodes>('.scatter')
      .data(this.scatterNodes);
    // Enter
    const scatterEnter = scatter.enter().append('g').attr('class', 'scatter');
    const scatterCircle = scatterEnter
      .append('circle')
      .attr('r', SCATTER_BALL_RADIUS);
    const scatterPath = scatterEnter
      .append('path')
      .attr('stroke-width', SCATTER_LINE_STROKE_WIDTH)
      .attr('fill', 'none');
    // Update
    scatter.merge(scatterEnter).attr('opacity', showScatter ? 1 : 0);
    scatter
      .select<SVGCircleElement>('circle')
      .merge(scatterCircle)
      .attr('cx', (d) => d?.x || 0)
      .attr('cy', (d) => d?.y || 0)
      .attr('fill', (d) => d.color);
    scatter
      .select<SVGPathElement>('path')
      .merge(scatterPath)
      .attr('d', () => {
        const lineData: [number, number][] = [
          [0, 0],
          [0, 0],
        ];
        return line(lineData);
      })
      .attr('stroke', (d) => d.color);
    const tick = () => {
      scatter
        .merge(scatterEnter)
        .attr('opacity', (d) =>
          d.id === this.hoverId || this.nodes.length === 1 ? 1 : 0,
        );
      scatter
        .select<SVGCircleElement>('circle')
        .merge(scatterCircle)
        .attr('cx', (d) => d?.x || 0)
        .attr('cy', (d) => d?.y || 0);
      scatter
        .select<SVGPathElement>('path')
        .merge(scatterPath)
        .attr('d', (d) => {
          const node = this.nodes.find((n) => n.label === d.label);
          const lineData: [number, number][] = [
            [d?.x || 0, d?.y || 0],
            [node?.x || 0, node?.y || 0],
          ];
          return line(lineData);
        });
    };
    // Exit
    scatter.exit().remove();

    if (showScatter) {
      this.scatterForce
        .nodes(this.scatterNodes)
        .force(
          'x',
          d3.forceX<ScatterNode>().x((d) => this.x(d.vote?.[0] ?? 0)),
        )
        .force(
          'y',
          d3.forceY<ScatterNode>().y((d) => this.y(d.vote?.[1] ?? 0)),
        );
    }
    this.scatterForce
      .restart()
      .alpha(1) // "Re-heat" the simulation to prevent charge decay
      .on('tick', tick);
  };

  /**
   * set current hover id and reset scatter nodes before applying forces
   * @param d selected node
   */
  private mouseOver = (d: Node) => {
    if (!d || d.score.some((coord) => coord === null || coord === undefined)) {
      return;
    }
    this.setHoverId(d.id);
    this.scatterNodes.forEach((n: ScatterNode) => {
      n.x = this.x(n?.score[0] as number) || 0;
      n.y = this.y(n?.score[1] as number) || 0;
    });
  };
  /**
   * Creates the nodes based on incoming votes
   */
  private updateBalls = (): void => {
    // Select
    const ball = d3
      .select(this.selector)
      .selectAll<SVGGElement, typeof this.nodes>('.ball')
      .data(this.nodes);
    // Enter
    const ballEnter = ball
      .enter()
      .append('g')
      .attr('class', 'ball')
      .attr('opacity', 0);
    const ballCircle = ballEnter
      .append('circle')
      .attr('class', 'ball-circle')
      .attr('r', BALL_RADIUS);
    const ballText = ballEnter
      .append('text')
      .attr('class', 'ball-text')
      .attr('dy', '0.35em')
      .attr('text-anchor', 'middle')
      .attr('font-size', BALL_RADIUS)
      .attr('font-weight', 600);
    const ballRect = ballEnter
      .append('rect') // Transparent rectangle for mouse events
      .attr('transform', `translate(${-BALL_RADIUS}, ${-BALL_RADIUS})`)
      .attr('width', BALL_RADIUS * 2)
      .attr('height', BALL_RADIUS * 2)
      .attr('fill', 'transparent');
    // Update
    if (this.hoverId !== null) {
      // Hide balls which are missing score values (i.e., initial results)
      ball
        .merge(ballEnter)
        .attr('opacity', (d) =>
          d.score.findIndex((s) => s === null) === -1 ? 1 : 0,
        );
    }
    ball
      .merge(ballEnter)
      .transition()
      .attr('opacity', (d) => {
        const hasScores = d.score.findIndex((s) => s === null) === -1;
        if (hasScores && this.hoverId && this.hoverId !== d.id) {
          return UNFOCUSED_BALL_OPACITY;
        }
        return hasScores ? 1 : 0;
      });
    ball
      .select<SVGCircleElement>('.ball-circle')
      .merge(ballCircle)
      .attr('fill', (_, i) => this.colors[i] ?? null)
      .transition()
      .attr('r', (d) =>
        this.hoverId === d.id ? BALL_RADIUS * BALL_HOVER_SCALE : BALL_RADIUS,
      );
    ball
      .select<SVGTextElement>('.ball-text')
      .merge(ballText)
      .text((_, i) => i + 1)
      .attr('fill', (_, i) => this.textColors[i] ?? null);
    ball
      .select<SVGRectElement>('rect')
      .merge(ballRect)
      .on('mouseover', (_event, d) => this.mouseOver(d))
      .on('mouseout', () => this.setHoverId(null));
    const tick = () => {
      ball
        .merge(ballEnter)
        .attr('transform', (d) => `translate(${d?.x || 0}, ${d?.y || 0})`);
    };
    // Exit
    ball.exit().remove();

    this.force
      .nodes(this.nodes)
      .force(
        'x',
        d3.forceX<Node>().x((d) => this.x(d.score[0] ?? 0)),
      )
      .force(
        'y',
        d3.forceY<Node>().y((d) => this.y(d.score[1] ?? 0)),
      );
    this.force
      .restart()
      .alpha(1) // "Re-heat" the simulation to prevent charge decay
      .on('tick', tick);
  };

  /**
   * Visualization updater
   *
   * @remarks
   *  Must resolve animations before other chained commands can be executed, like taking a screenshot with PhantomJS
   *
   * @returns resolved promise
   */
  public update = (): Promise<void> => {
    this.x
      .domain([this.range.min, this.range.max])
      .range([BALL_RADIUS, this.gridPositionAndSize.width - BALL_RADIUS]);
    this.y
      .domain([this.range.min, this.range.max])
      .range([this.gridPositionAndSize.height - BALL_RADIUS, BALL_RADIUS]);

    d3.select(this.selector).attr(
      'transform',
      `translate(${MARGIN.left}, ${MARGIN.top})`,
    );
    this.updateLines();
    this.updateAxis();
    this.updateGrid();
    this.updateLegend();
    if (this.respondents < MAX_VOTES_THRESHOLD) {
      this.updateScatter();
    }
    this.updateBalls();

    // Force a "tick" many times to compute a static layout for
    // screenshots, will eliminate transitions and closely approximate
    // accurate positions:
    // https://github.com/d3/d3-force#simulation_tick
    if (!this.enableAnimations) {
      this.force.tick(SCREENSHOT_NUM_FORCE_TICKS);
      this.scatterForce.tick(SCREENSHOT_NUM_FORCE_TICKS);
    }

    return Promise.resolve();
  };
}
